import { io } from 'socket.io-client';
import { useAuthStore } from '../store/useAuthStore';

// Trazas solo cuando se piden expresamente (Ola 7). Antes escribia en consola
// en cada conexion, cada evento y cada comprobacion de estado.
const DEPURAR = import.meta.env.DEV && import.meta.env.VITE_DEPURAR_SOCKET === 'true';
const traza = (...argumentos) => { if (DEPURAR) console.debug(...argumentos); };

class SocketService {
    constructor() {
        this.socket = null;
        this.isConnecting = false;
        
        // ✅ CORREGIDO: Usar misma URL que las APIs
        this.url = this.getSocketUrl();
        traza('🔍 SocketService: URL detectada:', this.url);
    }

    // ✅ CORREGIDO: Usar la misma base que las APIs
    getSocketUrl() {
        // ✅ USAR LA MISMA URL BASE QUE LAS APIS
        const baseUrl = window.location.origin; // el mismo origen que sirve la aplicacion
        traza('🌐 SocketService: Base URL (mismo que APIs):', baseUrl);
        return baseUrl;
    }

    connect() {
        if (this.isConnecting) {
            traza('🔌 SocketService: Ya hay una conexión en progreso, esperando...');
            return;
        }

        if (this.socket && this.socket.connected) {
            traza('🔌 SocketService: Desconectando socket existente antes de reconectar');
            this.socket.disconnect();
            this.socket = null;
        }

        this.isConnecting = true;
        traza('🔌 SocketService: Iniciando conexión a:', this.url);

        // El canal de eventos exige autenticacion desde la Ola 3: el servidor
        // deriva la sala del token y ya no acepta el userId que diga el cliente.
        const token = localStorage.getItem('gestas_access_token');

        this.socket = io(this.url, {
            // ✅ USAR LA MISMA RUTA QUE EL PROXY DE APIS
            path: '/socket.io/',
            auth: { token },
            forceNew: true,
            timeout: 20000,
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 2000,
            transports: ['websocket', 'polling'],
            withCredentials: true,
            autoConnect: true,
            upgrade: true,
            rememberUpgrade: true
        });

        this.socket.on('connect', () => {
            traza('✅ SocketService: Conectado exitosamente con ID:', this.socket.id);
            this.isConnecting = false;

            const user = useAuthStore.getState().user;
            if (user && user.id) {
                traza(`🚪 SocketService: Solicitando unirse a la sala para el usuario ${user.id}`);
                this.socket.emit('join_user_room', user.id);
            }
        });

        this.socket.on('disconnect', (reason) => {
            traza('❌ SocketService: Desconectado. Razón:', reason);
            this.isConnecting = false;
        });

        this.socket.on('connect_error', (error) => {
            console.error('❌ SocketService: Error de conexión:', error);
            this.isConnecting = false;
        });

        this.socket.on('prediction_progress', (data) => {
            this.socket.emit('internal_prediction_progress', data);
        });

        this.socket.on('prediction_complete', (data) => {
            this.socket.emit('internal_prediction_complete', data);
        });

        this.socket.on('pong', (data) => {
            traza('🏓 SocketService: Pong recibido:', data);
        });

        // El onAny anterior escribia en consola CADA evento recibido, incluidos
        // los de progreso de lote: 10.000 mensajes en un lote de 500 (Ola 7).

        return this.socket;
    }

    disconnect() {
        if (this.socket) {
            traza('🔌 SocketService: Desconectando socket');
            this.socket.disconnect();
            this.socket = null;
        }
        this.isConnecting = false;
    }

    getSocket() {
        return this.socket;
    }

    forceReconnect() {
        traza('🔄 SocketService: Forzando reconexión completa');
        this.disconnect();
        setTimeout(() => {
            this.connect();
        }, 500);
    }

    isConnected() {
        const connected = this.socket && this.socket.connected;
        return connected;
    }

    ensureConnection() {
        if (!this.isConnected() && !this.isConnecting) {
            traza('🔄 SocketService: Asegurando conexión...');
            this.connect();
        }
        return this.socket;
    }

    testConnection() {
        if (this.socket) {
            traza('🧪 SocketService: Enviando test ping...');
            this.socket.emit('ping', { timestamp: Date.now() });
            
            this.socket.once('pong', (data) => {
                traza('✅ SocketService: Test ping exitoso:', data);
            });
            
            setTimeout(() => {
                traza('⚠️ SocketService: Test ping timeout - posible problema de conexión');
            }, 5000);
        } else {
            traza('❌ SocketService: No hay socket para testear');
        }
    }
    
    on(event, callback) {
        if (this.socket) {
            this.socket.on(event, callback);
        }
    }

    off(event, callback) {
        if (this.socket) {
            this.socket.off(event, callback);
        }
    }

    emit(event, data) {
        if (this.socket) {
            this.socket.emit(event, data);
        }
    }
}

const socketService = new SocketService();
export default socketService;